import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { requestTelegramLink, getTelegramStatus, unlinkTelegram } from '../services/telegramService';

const ProfilePage = () => {
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [linkingInProgress, setLinkingInProgress] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [phone, setPhone] = useState(user?.phone || '');
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneMessage, setPhoneMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Charger le statut Telegram au montage du composant
  useEffect(() => {
    if (user) {
      loadTelegramStatus();
    }
  }, [user]);

  const loadTelegramStatus = async () => {
    try {
      setTelegramLoading(true);
      const status = await getTelegramStatus();
      setTelegramStatus(status);
    } catch (error) {
      console.error("Erreur lors du chargement du statut Telegram:", error);
      setTelegramStatus({ linked: false });
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleRequestTelegramLink = async () => {
    if (!user.phone) {
      alert("Aucun numéro de téléphone trouvé dans votre profil");
      return;
    }

    try {
      setLinkingInProgress(true);
      const response = await requestTelegramLink(user.phone);
      setVerificationCode(response.code);
      alert(`Code généré avec succès! Envoyez "${response.code}" au bot @beninagri_official_bot sur Telegram`);
    } catch (error) {
      console.error("Erreur lors de la demande de liaison:", error);
      alert("Erreur lors de la génération du code. Veuillez réessayer.");
    } finally {
      setLinkingInProgress(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    if (window.confirm("Êtes-vous sûr de vouloir délier votre compte Telegram ?")) {
      try {
        await unlinkTelegram();
        setTelegramStatus({ linked: false });
        alert("Compte Telegram délié avec succès");
      } catch (error) {
        console.error("Erreur lors de la déliaison:", error);
        alert("Erreur lors de la déliaison. Veuillez réessayer.");
      }
    }
  };

  const handleSavePhone = async () => {
    setSavingPhone(true);
    setPhoneMessage('');
    setPhoneError('');
    try {
      await api.put('/customer/profile', { phone });
      setPhoneMessage('Téléphone mis à jour avec succès !');

      setPhoneMessage('Téléphone mis à jour avec succès !');
      // Mettre à jour le localStorage avec le nouveau téléphone
      const updatedUser = { ...JSON.parse(localStorage.getItem('user') || '{}'), phone };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setTimeout(() => window.location.reload(), 1500);

    } catch (err: any) {
      setPhoneError(err.response?.data?.detail || 'Erreur lors de la mise à jour.');
    } finally {
      setSavingPhone(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Accès non autorisé</h2>
          <p className="mb-4">Veuillez vous connecter pour accéder à votre profil.</p>
          <a href="/login" className="bg-olive text-white py-2 px-4 rounded hover:bg-terracotta transition">
            Se connecter
          </a>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate mb-6">Mon Profil</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="md:w-1/4">
            <div className="text-center mb-6">
              <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-3 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold">{user.name}</h3>
              <p className="text-gray-600">{user.email || user.phone}</p>
              <div className="mt-2 text-sm">
                {user.is_farmer && <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full mr-1">Fermier</span>}
                {user.is_customer && <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full mr-1">Client</span>}
                {user.is_admin && <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full">Admin</span>}
              </div>
            </div>
            
            <div className="space-y-2">
              <button 
                onClick={() => setActiveTab('profile')}
                className={`w-full py-2 px-4 text-left rounded transition ${
                  activeTab === 'profile' ? 'bg-olive text-white' : 'text-olive hover:bg-gray-100'
                }`}
              >
                Informations personnelles
              </button>
              <button 
                onClick={() => setActiveTab('telegram')}
                className={`w-full py-2 px-4 text-left rounded transition ${
                  activeTab === 'telegram' ? 'bg-olive text-white' : 'text-olive hover:bg-gray-100'
                }`}
              >
                Notifications Telegram
              </button>
              <button 
                onClick={() => setActiveTab('orders')}
                className={`w-full py-2 px-4 text-left rounded transition ${
                  activeTab === 'orders' ? 'bg-olive text-white' : 'text-olive hover:bg-gray-100'
                }`}
              >
                Mes commandes
              </button>
              <button 
                onClick={() => setActiveTab('addresses')}
                className={`w-full py-2 px-4 text-left rounded transition ${
                  activeTab === 'addresses' ? 'bg-olive text-white' : 'text-olive hover:bg-gray-100'
                }`}
              >
                Adresses de livraison
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="md:w-3/4">
            {activeTab === 'profile' && (
              <div className="bg-beige p-6 rounded-lg">
                <h2 className="text-2xl font-semibold mb-4">Informations personnelles</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-1">Nom</label>
                    <input 
                      type="text" 
                      value={user.name} 
                      readOnly
                      className="w-full p-2 border border-gray-300 rounded bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">Email</label>
                    <input 
                      type="email" 
                      value={user.email || 'Non renseigné'} 
                      readOnly
                      className="w-full p-2 border border-gray-300 rounded bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">Téléphone</label>
                    {phoneMessage && <div className="text-green-600 text-sm mb-1">{phoneMessage}</div>}
                    {phoneError && <div className="text-red-600 text-sm mb-1">{phoneError}</div>}
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        value={phone}
                        onChange={e => setPhone(e.target.value)}
                        className="flex-1 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-olive"
                        placeholder="+22901XXXXXXXX"
                      />
                      <button
                        onClick={handleSavePhone}
                        disabled={savingPhone}
                        className="bg-olive text-white px-4 py-2 rounded hover:bg-terracotta transition disabled:opacity-50"
                      >
                        {savingPhone ? '...' : 'Sauver'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Requis pour activer les notifications Telegram</p>
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">Type de compte</label>
                    <input 
                      type="text" 
                      value={`${user.is_farmer ? 'Fermier' : ''} ${user.is_customer ? 'Client' : ''} ${user.is_admin ? 'Admin' : ''}`.trim()} 
                      readOnly
                      className="w-full p-2 border border-gray-300 rounded bg-gray-50"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'telegram' && (
              <div className="bg-beige p-6 rounded-lg">
                <h2 className="text-2xl font-semibold mb-4">Notifications Telegram</h2>
                
                {telegramLoading ? (
                  <div className="text-center py-4">
                    <div className="text-lg">Vérification du statut...</div>
                  </div>
                ) : (
                  <div>
                    {telegramStatus?.linked ? (
                      // Compte lié
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center mb-2">
                          <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <h3 className="text-lg font-semibold text-green-800">Compte Telegram lié</h3>
                        </div>
                        <p className="text-green-700 mb-4">
                          Votre compte est lié à Telegram. Vous recevrez des notifications pour:
                        </p>
                        <ul className="list-disc list-inside text-green-700 mb-4">
                          <li>Réinitialisation de mot de passe</li>
                          <li>Mises à jour importantes du compte</li>
                          <li>Annonces de la plateforme</li>
                        </ul>
                        <button 
                          onClick={handleUnlinkTelegram}
                          className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition"
                        >
                          Délier le compte Telegram
                        </button>
                      </div>
                    ) : (
                      // Compte non lié
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center mb-2">
                          <svg className="w-5 h-5 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <h3 className="text-lg font-semibold text-yellow-800">Compte Telegram non lié</h3>
                        </div>
                        <p className="text-yellow-700 mb-4">
                          Liez votre compte Telegram pour recevoir des notifications importantes directement sur votre téléphone.
                        </p>
                        
                        {user.phone ? (
                          <div>
                            <p className="text-gray-700 mb-2">
                              <strong>Téléphone associé:</strong> {user.phone}
                            </p>
                            
                            {verificationCode && (
                              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                                <p className="text-blue-800 font-semibold">Code de vérification généré:</p>
                                <p className="text-2xl font-mono text-blue-900 my-2">{verificationCode}</p>
                                <p className="text-blue-700 text-sm">
                                  Envoyez ce code à <a href="https://t.me/beninagri_official_bot" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 underline">@beninagri_official_bot</a> sur Telegram pour lier votre compte.
                                </p>
                              </div>
                            )}
                            
                            <div className="flex flex-col sm:flex-row gap-3">
                              <button 
                                onClick={handleRequestTelegramLink}
                                disabled={linkingInProgress}
                                className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {linkingInProgress ? 'Génération...' : 'Générer un code de liaison'}
                              </button>
                              
                              <Link 
                                to="/telegram/setup"
                                className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition text-center"
                              >
                                Configuration guidée
                              </Link>
                            </div>
                            
                            <div className="mt-4 text-sm text-gray-600">
                              <p><strong>Instructions:</strong></p>
                              <ol className="list-decimal list-inside mt-2 space-y-1">
                                <li>Cliquez sur "Générer un code de liaison"</li>
                                <li>Ouvrez Telegram et recherchez <a href="https://t.me/beninagri_official_bot" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 underline">@beninagri_official_bot</a></li>
                                <li>Envoyez le code généré au bot</li>
                                <li>Votre compte sera automatiquement lié</li>
                              </ol>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-red-50 border border-red-200 rounded p-3">
                            <p className="text-red-800">
                              Aucun numéro de téléphone trouvé dans votre profil. 
                              Veuillez contacter l'administrateur pour ajouter votre numéro.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'orders' && (
              <div className="bg-beige p-6 rounded-lg">
                <h2 className="text-2xl font-semibold mb-4">Mes commandes</h2>
                <p className="text-gray-600">Cette section sera bientôt disponible.</p>
              </div>
            )}

            {activeTab === 'addresses' && (
              <div className="bg-beige p-6 rounded-lg">
                <h2 className="text-2xl font-semibold mb-4">Adresses de livraison</h2>
                <p className="text-gray-600">Cette section sera bientôt disponible.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;